import { useTranslation } from 'react-i18next'
import { CollapsedDisclaimer } from '../compliance/CollapsedDisclaimer'
import { sanitizeCoachText } from '../../lib/aiResponseValidator'
import { KILO } from '../../constants/KILO_THEME'

type DailyOverrideBannerProps = {
  briefingText: string | null
  workoutAdjustments: Record<string, unknown>
  onAccept: () => void
  onReject: () => void
  busy?: boolean
}

const DailyOverrideBanner = ({
  briefingText,
  workoutAdjustments,
  onAccept,
  onReject,
  busy = false,
}: DailyOverrideBannerProps) => {
  const { t } = useTranslation()
  const volumePct = workoutAdjustments.workout_volume_adjustment_pct as number | undefined
  const restDay = Boolean(workoutAdjustments.rest_day)
  const safeBriefing = sanitizeCoachText(briefingText, 'morning_briefing')
  const volumeSign = volumePct != null && volumePct > 0 ? '+' : ''

  return (
    <div className={KILO.embeddedSection}>
      <p className={KILO.sectionLabel}>{t('coach.dailyOverride.title')}</p>
      {safeBriefing ? (
        <p className={`mt-2 text-sm leading-relaxed ${KILO.text.primary}`}>{safeBriefing}</p>
      ) : (
        <p className={`mt-2 text-sm ${KILO.text.muted}`}>{t('coach.dailyOverride.fallbackBody')}</p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {restDay ? (
          <span className="rounded-full border border-kilo-line bg-kilo-card px-2 py-0.5 text-xs text-kilo-text">
            {t('coach.dailyOverride.suggestedRestDay')}
          </span>
        ) : null}
        {volumePct != null ? (
          <span className={KILO.volumeBadge}>
            {t('coach.dailyOverride.volume', { sign: volumeSign, pct: volumePct })}
          </span>
        ) : null}
      </div>
      <div className="mt-4 flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onAccept}
          className="flex h-10 flex-1 items-center justify-center rounded-xl bg-kilo-accent text-sm font-bold text-black disabled:opacity-50"
        >
          {t('coach.dailyOverride.accept')}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onReject}
          className="flex h-10 flex-1 items-center justify-center rounded-xl border border-neutral-300 bg-transparent text-sm font-medium text-neutral-700 transition-colors hover:border-neutral-400 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-600 dark:text-neutral-300 dark:hover:border-neutral-500 dark:hover:bg-neutral-800"
        >
          {t('coach.dailyOverride.keepBasePlan')}
        </button>
      </div>
      <CollapsedDisclaimer />
    </div>
  )
}

export default DailyOverrideBanner
